// Generated by GitHub Copilot
import React, { useState, useEffect } from "react";
import { FaUserPlus, FaSave, FaTimes } from "react-icons/fa";
import PrivilegeToggle from "../PrivilegeManager/PrivilegeToggle";
import FeatureToggle from "../FeatureManager/FeatureToggle";
import { getFeatureToggleDefinitions } from "../../../connections/featureDefinitions";
import ConfirmDialog from "../../common/ConfirmDialog/ConfirmDialog";
import styles from "./CreateUserModal.module.css";
import { trackUserCreate, trackPrivilegeChange } from "../../../utils/modernUITracking";

/**
 * Create User Modal Component
 */
const CreateUserModal = ({ show, onClose, onUserCreated, currentUser }) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    privileges: {},
    features: {},
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Available privileges and features (these would typically come from an API)
  const availablePrivileges = [
    {
      name: "editor",
      displayName: "Editor",
      description: "Can edit problems and notes",
      riskLevel: "low",
    },
    {
      name: "create",
      displayName: "Create",
      description: "Can create new problems",
      riskLevel: "low",
    },
    {
      name: "search",
      displayName: "Search",
      description: "Can search across all data",
      riskLevel: "medium",
    },
    {
      name: "revert",
      displayName: "Revert",
      description: "Can revert problem versions",
      riskLevel: "medium",
    },
    {
      name: "archive",
      displayName: "Archive",
      description: "Can archive problems",
      riskLevel: "high",
      dependencies: [],
    },
    {
      name: "configuration",
      displayName: "Configuration",
      description: "Can edit delivery streams and other system configuration",
      riskLevel: "medium",
    },
    {
      name: "interested_parties",
      displayName: "View Interested Parties",
      description: "Can view interested parties information in user records",
      riskLevel: "low",
    },
    {
      name: "impact",
      displayName: "Impact",
      description:
        "Can create, edit, and delete impact records in Impact Reporting",
      riskLevel: "low",
    },
    {
      name: "modern_status_page",
      displayName: "Modern Status Page",
      description: "Access to modern StatusPage features and interface",
      riskLevel: "low",
    },
    {
      name: "deity",
      displayName: "Deity",
      description: "Full system access - use with extreme caution",
      riskLevel: "high",
    },
  ];

  // Available features will be loaded from API
  const [availableFeatures, setAvailableFeatures] = useState([]);

  // Generated by GitHub Copilot
  // Load available features from API
  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const featureDefinitions = await getFeatureToggleDefinitions();

        // Convert API format to the format expected by the component
        const features = featureDefinitions.map((def) => ({
          name: def.name,
          displayName: def.displayName,
          description: def.description,
          category: def.category,
          experimental: def.experimental,
          beta: def.beta,
        }));

        setAvailableFeatures(features);
      } catch (error) {
        console.error("Failed to load feature definitions:", error);
        // Set empty array on error to prevent UI issues
        setAvailableFeatures([]);
      }
    };

    loadFeatures();
  }, []);

  // Reset form when modal is opened
  useEffect(() => {
    if (show) {
      setFormData({
        email: "",
        name: "",
        privileges: {},
        features: {},
      });
      setValidationErrors({});
      setSubmitError(null);
    }
  }, [show]);

  /**
   * Handle form field changes
   */
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  /**
   * Handle privilege changes
   */
  const handlePrivilegeChange = (privilege, enabled) => {
    setFormData((prev) => ({
      ...prev,
      privileges: {
        ...prev.privileges,
        [privilege]: enabled,
      },
    }));

    // Track privilege toggle (find risk level from availablePrivileges)
    const privilegeInfo = availablePrivileges.find(p => p.name === privilege);
    trackPrivilegeChange(privilege, enabled, {
      userEmail: formData.email || 'new_user',
      adminEmail: currentUser.email,
      riskLevel: privilegeInfo?.riskLevel || 'unknown'
    });
  };

  /**
   * Handle feature changes
   */
  const handleFeatureChange = (feature, enabled) => {
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [feature]: enabled,
      },
    }));
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const errors = {};

    // Email validation
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Name validation
    if (!formData.name || formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters long";
    }

    // Privilege validation
    const selectedPrivileges = Object.entries(formData.privileges)
      .filter(([key, value]) => value === true)
      .map(([key]) => key);

    if (selectedPrivileges.includes("deity") && !currentUser.privileges.deity) {
      errors.privileges = "Only deity users can assign deity privileges";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch("/api/v1/config/users", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          name: formData.name.trim(),
          privileges: formData.privileges,
          features: formData.features,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            `Failed to create user: ${response.status} ${response.statusText}`
        );
      }

      const createdUser = await response.json();

      // Track successful user creation
      trackUserCreate(formData.email.trim(), {
        adminEmail: currentUser.email,
        success: true,
        privileges: formData.privileges,
        features: formData.features,
        privilegeCount: Object.keys(formData.privileges).filter(k => formData.privileges[k]).length,
        featureCount: Object.keys(formData.features).filter(k => formData.features[k]).length
      });

      onUserCreated(createdUser);
    } catch (error) {
      console.error("Error creating user:", error);

      // Track failed user creation
      trackUserCreate(formData.email.trim(), {
        adminEmail: currentUser.email,
        success: false,
        error: error.message
      });

      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = () => {
    // Check if form has data before closing
    const hasData = formData.email || formData.name ||
      Object.values(formData.privileges).some(v => v) ||
      Object.values(formData.features).some(v => v);

    if (hasData) {
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  /**
   * Handle confirm dialog close - user confirmed discard
   */
  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    onClose();
  };

  /**
   * Handle confirm dialog cancel - user wants to keep editing
   */
  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  if (!show) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContainer}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaUserPlus className={styles.modalIcon} />
            <span>Create New User</span>
          </h2>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            aria-label="Close"
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.modalForm}>
          <div className={styles.modalBody}>
            {submitError && (
              <div className={`${styles.alert} ${styles.alertDanger}`}>
                {submitError}
              </div>
            )}

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  className={`${styles.formInput} ${
                    validationErrors.email ? styles.invalid : ""
                  }`}
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="user@coxautoinc.com"
                  disabled={isSubmitting}
                  required
                />
                {validationErrors.email && (
                  <span className={styles.formError}>
                    {validationErrors.email}
                  </span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  className={`${styles.formInput} ${
                    validationErrors.name ? styles.invalid : ""
                  }`}
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  disabled={isSubmitting}
                  required
                />
                {validationErrors.name && (
                  <span className={styles.formError}>
                    {validationErrors.name}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.cardContainer}>
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h6 className={styles.cardTitle}>Privileges</h6>
                  {validationErrors.privileges && (
                    <small className={styles.cardError}>
                      {validationErrors.privileges}
                    </small>
                  )}
                </div>
                <div className={styles.cardBody}>
                  {availablePrivileges.map((privilege) => (
                    <PrivilegeToggle
                      key={privilege.name}
                      privilege={privilege}
                      enabled={formData.privileges[privilege.name] || false}
                      onChange={handlePrivilegeChange}
                      disabled={
                        isSubmitting ||
                        (privilege.name === "deity" &&
                          !currentUser.privileges.deity)
                      }
                    />
                  ))}
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h6 className={styles.cardTitle}>Features</h6>
                </div>
                <div className={styles.cardBody}>
                  {availableFeatures.map((feature) => (
                    <FeatureToggle
                      key={feature.name}
                      feature={feature}
                      enabled={formData.features[feature.name] || false}
                      onChange={handleFeatureChange}
                      disabled={isSubmitting}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <FaTimes className={styles.buttonIcon} />
              <span>Cancel</span>
            </button>

            <button
              type="submit"
              className={`${styles.button} ${styles.buttonPrimary}`}
              disabled={isSubmitting}
            >
              <FaSave className={styles.buttonIcon} />
              <span>{isSubmitting ? "Creating User..." : "Create User"}</span>
            </button>
          </div>
        </form>

        <ConfirmDialog
          show={showConfirmDialog}
          title="Discard Changes?"
          message="You have unsaved changes. Are you sure you want to discard them?"
          confirmLabel="Discard Changes"
          cancelLabel="Keep Editing"
          onConfirm={handleConfirmClose}
          onCancel={handleCancelClose}
          variant="danger"
        />
      </div>
    </div>
  );
};

export default CreateUserModal;
